import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CqrsService } from '@common/services/cqrs.service';
import { User } from '@common/models/User';
import { VesselVisitDocumentStatusCodes } from '@common/classes/vessel-visit-document-status-codes';
import { VesselVisitDocumentTypeCodes } from '@common/classes/vessel-visit-document-types';
import { AdministrationPermissions, VesselVisitDocumentPermissions } from '@common/classes/permissions';
import { DocumentDirectionCodes } from '@common/classes/document-direction-codes';

@Injectable({
  providedIn: 'root'
})
export class VesselVisitDocumentEditableCheckService {
  editPermission = VesselVisitDocumentPermissions.Action.Edit;

  constructor(
    private cqrs: CqrsService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) {}

  async canEdit(id: string, user: User): Promise<boolean> {
    const model = await this.cqrs.query('VesselVisitDocument', { id });    
    
    // some checks are made on backend to see if user can edit vessel visit document, if he can't null is returned
    if(model === undefined || model === null){
      this.toastrService.error(this.translateService.instant('User does not have neccessary permission to edit vessel visit documents'));
      return false;
    }

    if (!this.hasEditPermission(user)) {
      this.toastrService.error(this.translateService.instant('User does not have neccessary permission to edit vessel visit documents'));
      return false;
    }

    if (!this.isEditable(model, user)) {
      this.toastrService.error(this.translateService.instant('Vessel visit document is not editable!'));
      return false;
    }

    return this.isEditable(model, user);
  }

  hasEditPermission(user: User, editPermission?: string): boolean {
    if (editPermission) {
      return user.hasPermission(editPermission);
    }
    return true;
  }

  isEditable(model: any, user: User): boolean {
    const canViewOwnDocuments = model?.createdBy?.organizationId === user.organizationId &&
      user.hasPermission(VesselVisitDocumentPermissions.View.Own);

    const canViewAll = this.isAdminUser(user) ||
      user.hasPermission(VesselVisitDocumentPermissions.View.All);

    const validStatus =
      model.status?.id === VesselVisitDocumentStatusCodes.InProgress ||
      model.status?.id === VesselVisitDocumentStatusCodes.Rejected;    

    let agentCheck =     
      (model.vesselVisit.arrivalAgent?.id == user.organizationId &&
        (model.documentType.direction.id == DocumentDirectionCodes.ARRIVAL ||
         model.documentType.direction.id == DocumentDirectionCodes.OTHER))
      ||
      (model.vesselVisit.departureAgent.id == user.organizationId &&
        (model.documentType.direction.id == DocumentDirectionCodes.DEPARTURE ||
         model.documentType.direction.id == DocumentDirectionCodes.OTHER));
         
    let canEdit = (canViewOwnDocuments || canViewAll || agentCheck) && validStatus;

    if (
      model.documentTypeId === VesselVisitDocumentTypeCodes.AR &&
      model.accommodationRequests?.length > 0 &&
      model.accommodationRequests[0].berthProposals?.length > 0
    ) {
      const lastProposal = model.accommodationRequests[0].berthProposals.reduce(
        (prev, curr) => (curr.id > prev.id ? curr : prev)
      );

      if (
        model.status?.id === VesselVisitDocumentStatusCodes.InProgress ||
        lastProposal.accepted == null ||
        !lastProposal.accepted
      ) {
        canEdit = true;
      } else {
        canEdit = false;
      }
    }

    return canEdit;
  }

    isAdminUser(user: User){
        return user.isSystemUser || user.hasPermission(AdministrationPermissions.User.ViewAll);
    }  
}